import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import {
  kdsGetDevicesByLocation,
  printerGetAllByLocationId,
  stationsDeleteStation,
  stationsGetByLocationId,
  stationsPairDevice,
  stationsUnpairDevice,
  stationsUpdate,
} from "~/api-refs";
import type {
  AudioSettingsState,
  AutoPrint,
  CrossScreenMode,
  DisplayOptionsState,
  ModifierKeywordsState,
  PrintType,
  RoutingMode,
  SortMode,
  StationRole,
  StationType,
  TimingThresholdsState,
} from "~/components/stations/station-edit-types";
import {
  buildStationUpdatePayload,
  getDefaultAudioSettings,
  getDefaultDisplayOptions,
  getDefaultModifierKeywords,
  getDefaultTimingThresholds,
  getStationStatus,
  getStatusBadge,
  hasAnyStationChanges,
} from "~/components/stations/station-edit-types";
import { convexClient } from "~/integrations/convex/provider";
import { logger } from "~/lib/logger";
import { ProductEvents } from "~/lib/posthog";

export function useStationEdit(station: StationType, handleClose: () => void) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Basic editable state
  const [name, setName] = useState(station.name);
  const [color, setColor] = useState(station.color);
  const [hasScreen, setHasScreen] = useState(station.hasScreen);
  const [routingMode, setRoutingMode] = useState<RoutingMode>(
    station.routingMode
  );
  const [selectedPrinterIds, setSelectedPrinterIds] = useState<string[]>(
    station.printerIds ?? []
  );
  const [autoPrint, setAutoPrint] = useState<AutoPrint>(
    station.printSettings?.autoPrint ?? "off"
  );
  const [printType, setPrintType] = useState<PrintType>(
    station.printSettings?.printType ?? "ticket"
  );
  const [printOrderTypes, setPrintOrderTypes] = useState<string[]>(
    station.printSettings?.printOrderTypes ?? ["dineIn", "takeout", "delivery"]
  );
  const [categoriesSelected, setCategoriesSelected] = useState<string[]>(
    station.categoryIds ?? []
  );
  const [itemsSelected, setItemsSelected] = useState<string[]>(
    station.itemIds ?? []
  );
  const [isRoutingDialogOpen, setIsRoutingDialogOpen] = useState(false);

  // Display, audio, timing state
  const [displayOptions, setDisplayOptions] = useState<DisplayOptionsState>(
    () => getDefaultDisplayOptions(station)
  );
  const [audioSettings, setAudioSettings] = useState<AudioSettingsState>(() =>
    getDefaultAudioSettings(station)
  );
  const [timingThresholds, setTimingThresholds] =
    useState<TimingThresholdsState>(() => getDefaultTimingThresholds(station));

  // Cross-Screen Mode state
  const [crossScreenMode, setCrossScreenMode] = useState<CrossScreenMode>(
    station.crossScreenMode ?? "independent"
  );
  const [nextStationId, setNextStationId] = useState<string | null>(
    station.nextStationId ?? null
  );
  const [stationRole, setStationRole] = useState<StationRole>(
    station.stationRole ?? null
  );

  // Advanced settings state
  const [modifierKeywords, setModifierKeywords] =
    useState<ModifierKeywordsState>(() => getDefaultModifierKeywords(station));
  const [sortMode, setSortMode] = useState<SortMode>(
    station.sortMode ?? "fifo"
  );
  const [createTicketPerSend, setCreateTicketPerSend] = useState(
    station.createTicketPerSend ?? false
  );

  // Collapsible section states
  const [isDisplayOpen, setIsDisplayOpen] = useState(false);
  const [isAudioOpen, setIsAudioOpen] = useState(false);
  const [isTimingOpen, setIsTimingOpen] = useState(false);
  const [isWorkflowOpen, setIsWorkflowOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Fetch printers for this location
  const { data: printers } = useQuery(
    convexQuery(printerGetAllByLocationId, {
      locationId: station.locationId,
    })
  );

  const printersAvailable =
    printers?.map((p) => ({ value: p._id, label: p.name })) ?? [];

  // Fetch all KDS devices for this location (for pairing)
  const { data: allDevices } = useQuery({
    ...convexQuery(kdsGetDevicesByLocation, {
      locationId: station.locationId,
    }),
    enabled: hasScreen,
  });

  const unpairedDevices =
    allDevices?.filter(
      (device) => !device.stationId || device.stationId !== station._id
    ) ?? [];

  // Fetch all stations for cross-screen mode selection
  const { data: allStations } = useQuery(
    convexQuery(stationsGetByLocationId, {
      locationId: station.locationId,
    })
  );

  const otherStations = allStations?.filter((s) => s._id !== station._id) ?? [];

  // Device pairing mutations
  const pairDeviceMutation = useMutation({
    mutationFn: (data: {
      stationId: Id<"stations">;
      kdsDeviceId: Id<"kdsDevices">;
    }) => convexClient.mutation(stationsPairDevice, data),
    onSuccess: () => {
      toast.success("Device paired successfully");
    },
    onError: () => {
      toast.error("Failed to pair device");
    },
  });

  const unpairDeviceMutation = useMutation({
    mutationFn: (data: { kdsDeviceId: Id<"kdsDevices"> }) =>
      convexClient.mutation(stationsUnpairDevice, data),
    onSuccess: () => {
      toast.success("Device unpaired");
    },
    onError: () => {
      toast.error("Failed to unpair device");
    },
  });

  // Update mutation
  const updateStationMutation = useMutation({
    mutationFn: (data: ReturnType<typeof buildStationUpdatePayload>) =>
      convexClient.mutation(stationsUpdate, data),
  });

  // Delete mutation
  const deleteStationMutation = useMutation({
    mutationFn: (data: { id: Id<"stations"> }) =>
      convexClient.mutation(stationsDeleteStation, data),
  });

  // Set default station mutation
  const setDefaultStationMutation = useMutation({
    mutationFn: (data: { id: Id<"stations"> }) =>
      convexClient.mutation(stationsUpdate, { id: data.id, isDefault: true }),
    onSuccess: () => {
      toast.success("Default station updated");
    },
    onError: () => {
      toast.error("Failed to set default station");
    },
  });

  const hasChanges = hasAnyStationChanges(station, {
    name,
    color,
    hasScreen,
    routingMode,
    selectedPrinterIds,
    autoPrint,
    printType,
    printOrderTypes,
    categoriesSelected,
    itemsSelected,
    displayOptions,
    audioSettings,
    timingThresholds,
    crossScreenMode,
    nextStationId,
    stationRole,
    modifierKeywords,
    sortMode,
    createTicketPerSend,
  });

  const handleSave = async () => {
    if (updateStationMutation.isPending || !hasChanges) {
      return;
    }

    const payload = buildStationUpdatePayload({
      station,
      name,
      color,
      hasScreen,
      routingMode,
      selectedPrinterIds,
      autoPrint,
      printType,
      printOrderTypes,
      categoriesSelected,
      itemsSelected,
      displayOptions,
      audioSettings,
      timingThresholds,
      crossScreenMode,
      modifierKeywords,
      sortMode,
      createTicketPerSend,
      nextStationId,
      stationRole,
    });

    try {
      await updateStationMutation.mutateAsync(payload);
      ProductEvents.stationUpdated(station._id);
      toast.success("Station updated successfully");
      handleClose();
    } catch (error) {
      logger.exception("station_update_failed", error);
      toast.error("Failed to update station");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteStationMutation.mutateAsync({ id: station._id });
      ProductEvents.stationDeleted(station._id);
      toast.success("Station deleted");
      handleClose();
    } catch (error) {
      logger.exception("station_delete_failed", error);
      toast.error("Failed to delete station");
    }
  };

  const status = getStationStatus(station);
  const statusBadge = getStatusBadge(status);

  return {
    // Basic state
    name,
    setName,
    color,
    setColor,
    hasScreen,
    setHasScreen,
    routingMode,
    setRoutingMode,
    selectedPrinterIds,
    setSelectedPrinterIds,
    autoPrint,
    setAutoPrint,
    printType,
    setPrintType,
    printOrderTypes,
    setPrintOrderTypes,
    categoriesSelected,
    setCategoriesSelected,
    itemsSelected,
    setItemsSelected,
    isRoutingDialogOpen,
    setIsRoutingDialogOpen,

    // Display/audio/timing
    displayOptions,
    setDisplayOptions,
    audioSettings,
    setAudioSettings,
    timingThresholds,
    setTimingThresholds,

    // Cross-screen
    crossScreenMode,
    setCrossScreenMode,
    nextStationId,
    setNextStationId,
    stationRole,
    setStationRole,

    // Advanced
    modifierKeywords,
    setModifierKeywords,
    sortMode,
    setSortMode,
    createTicketPerSend,
    setCreateTicketPerSend,

    // Collapsible sections
    isDisplayOpen,
    setIsDisplayOpen,
    isAudioOpen,
    setIsAudioOpen,
    isTimingOpen,
    setIsTimingOpen,
    isWorkflowOpen,
    setIsWorkflowOpen,
    isAdvancedOpen,
    setIsAdvancedOpen,

    // Delete dialog
    isDeleteOpen,
    setIsDeleteOpen,

    // Queries
    printersAvailable,
    allDevices,
    unpairedDevices,
    otherStations,

    // Mutations
    pairDeviceMutation,
    unpairDeviceMutation,
    updateStationMutation,
    deleteStationMutation,
    setDefaultStationMutation,

    // Computed
    hasChanges,
    statusBadge,

    // Handlers
    handleSave,
    handleDelete,
  };
}
